import { openDB, type IDBPDatabase } from "idb";
import { CURRENT_SCHEMA_VERSION } from "../app/constants";
import type { MoveInReportDb } from "./schema";

const DB_NAME = "moveinreport-local";
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase<MoveInReportDb>> | undefined;

export function getDb(): Promise<IDBPDatabase<MoveInReportDb>> {
  dbPromise ??= openDB<MoveInReportDb>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains("reports")) {
        const reports = db.createObjectStore("reports", { keyPath: "id" });
        reports.createIndex("byUpdatedAt", "updatedAt");
      }

      if (!db.objectStoreNames.contains("rooms")) {
        const rooms = db.createObjectStore("rooms", { keyPath: "id" });
        rooms.createIndex("byReportId", "reportId");
        rooms.createIndex("byReportSort", ["reportId", "sortOrder"]);
      }

      if (!db.objectStoreNames.contains("issues")) {
        const issues = db.createObjectStore("issues", { keyPath: "id" });
        issues.createIndex("byReportId", "reportId");
        issues.createIndex("byRoomId", "roomId");
        issues.createIndex("byRoomSort", ["roomId", "sortOrder"]);
        issues.createIndex("byReportStatus", ["reportId", "transcriptionStatus"]);
      }

      if (!db.objectStoreNames.contains("mediaBlobs")) {
        const media = db.createObjectStore("mediaBlobs", { keyPath: "key" });
        media.createIndex("byReportId", "reportId");
        media.createIndex("byIssueId", "issueId");
        media.createIndex("byRoomId", "roomId");
      }

      if (oldVersion > 0 && oldVersion < 3 && db.objectStoreNames.contains("transcriptionQueue")) {
        db.deleteObjectStore("transcriptionQueue");
      }

      if (!db.objectStoreNames.contains("transcriptionQueue")) {
        const queue = db.createObjectStore("transcriptionQueue", { keyPath: "jobId" });
        queue.createIndex("byReportId", "reportId");
        queue.createIndex("byNextAttemptAt", "nextAttemptAt");
        queue.createIndex("byIssueId", "issueId");
        queue.createIndex("byStatus", "status");
      }

      if (!db.objectStoreNames.contains("appSettings")) {
        db.createObjectStore("appSettings", { keyPath: "key" });
      }

      if (!db.objectStoreNames.contains("analyticsQueue")) {
        const analyticsQueue = db.createObjectStore("analyticsQueue", { keyPath: "eventId" });
        analyticsQueue.createIndex("byQueuedAt", "queuedAt");
        analyticsQueue.createIndex("byPriority", ["priority", "queuedAt"]);
      }
    },
    blocked() {
      console.warn("MoveInReport database upgrade is blocked by another tab.");
    },
    blocking() {
      console.warn("MoveInReport database is blocking a newer tab from upgrading.");
    }
  });

  return dbPromise;
}

export function getCurrentSchemaVersion(): number {
  return CURRENT_SCHEMA_VERSION;
}
